<script setup lang="ts">
import { computed } from 'vue'
import { Bitcoin, Check, Ticket, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { differenceInCalendarDays } from 'date-fns'
import { useRouter } from 'vue-router'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { DynamicModalType, SubscriptionType } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import Tools from '@/utils/tools'
import EnergyIcon from '@/icons/EnergyIcon.vue'

interface Props {
	ticket: number
	price: { usd: number, eur: number }
	g2a: string
	crypto: boolean
	month: number
	limited?: number
	type: SubscriptionType
	basePrice: number
	energy: number
	imagePerDay: number
	usd: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()

const authStore = useAuthStore()
const router = useRouter()

const { user } = storeToRefs(authStore)

// Computed properties for features
const features = computed(() => {
	const baseFeatures = [
		{
			key: 'eco_mode',
			link: true,
			icon: props.type === SubscriptionType.Pro ? Check : X,
			iconClass: props.type === SubscriptionType.Pro
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type === SubscriptionType.Pro ? 'text-slate-200 font-black hover:text-slate-400' : 'text-red-400 hover:text-red-200',
			text: props.type === SubscriptionType.Pro ? t('shop.subscription.unlimited_eco_mode') : t('shop.subscription.no_eco_mode'),
			available: props.type === SubscriptionType.Pro,
		},
	]

	const conditionalFeatures = [
		{
			key: 'all_styles',
			link: false,
			icon: props.type !== SubscriptionType.Free ? Check : X,
			iconClass: props.type !== SubscriptionType.Free
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type !== SubscriptionType.Free ? 'text-slate-200' : 'text-red-400',
			text: props.type !== SubscriptionType.Free ? t('shop.subscription.all_styles') : t('shop.subscription.2_styles'),
		},
		{
			key: 'style_conversion',
			link: false,
			icon: props.type !== SubscriptionType.Free ? Check : X,
			iconClass: props.type !== SubscriptionType.Free
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type !== SubscriptionType.Free ? 'text-slate-200' : 'text-red-400',
			text: props.type !== SubscriptionType.Free ? t('generator.style_convert') : t('shop.subscription.no_style_convert'),
		},
		{
			key: 'watermark',
			link: false,
			icon: props.type !== SubscriptionType.Free ? Check : X,
			iconClass: props.type !== SubscriptionType.Free
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type !== SubscriptionType.Free ? 'text-slate-200' : 'text-red-400',
			text: props.type !== SubscriptionType.Free ? t('shop.subscription.no_watermark') : t('shop.subscription.pay_remove_watermark'),
		},
		{
			key: 'private_images',
			link: false,
			icon: props.type !== SubscriptionType.Free ? Check : X,
			iconClass: props.type !== SubscriptionType.Free
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type !== SubscriptionType.Free ? 'text-slate-200' : 'text-red-400',
			text: props.type !== SubscriptionType.Free ? t('shop.subscription.private_images') : t('shop.subscription.no_private_images'),
			available: props.type !== SubscriptionType.Free,
		},
		{
			key: 'generation_count',
			link: false,
			icon: props.type === SubscriptionType.Free ? X : Check,
			iconClass: getGenerationIconClass(),
			textClass: props.type === SubscriptionType.Free ? 'text-red-400' : 'text-slate-200',
			text: getGenerationText(),
			available: props.type !== SubscriptionType.Free,
		},
		{
			key: 'resolution',
			link: false,
			icon: props.type === SubscriptionType.Free ? X : Check,
			iconClass: getResolutionIconClass(),
			textClass: props.type === SubscriptionType.Free ? 'text-red-400' : 'text-slate-200',
			text: getResolutionText(),
			available: props.type !== SubscriptionType.Free,
		},
		{
			key: 'no_tickets_required',
			link: false,
			icon: props.type >= SubscriptionType.Basic ? Check : X,
			iconClass: props.type >= SubscriptionType.Basic
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type >= SubscriptionType.Basic ? 'text-slate-200' : 'text-red-400',
			text: props.type >= SubscriptionType.Basic ? t('shop.subscription.no_tickets_req') : t('shop.subscription.tickets_req'),
			available: props.type >= SubscriptionType.Basic,
		},
		/* 		{
			key: 'bulk_download',
			icon: props.type !== SubscriptionType.Free ? Check : X,
			iconClass: props.type !== SubscriptionType.Free
				? 'p-1 rounded-full bg-purple-500/20 flex-center'
				: 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400',
			textClass: props.type !== SubscriptionType.Free ? 'text-slate-200' : 'text-red-400',
			text: props.type !== SubscriptionType.Free ? 'Bulk Download' : 'No Bulk Download',
			available: props.type !== SubscriptionType.Free,
		}, */

	]

	return [...baseFeatures, ...conditionalFeatures]
})

// Helper functions
function getGenerationIconClass() {
	if (props.type === SubscriptionType.Pro)
		return 'p-1 rounded-full bg-purple-500/50 flex-center'
	if (props.type === SubscriptionType.Basic)
		return 'p-1 rounded-full bg-purple-500/20 flex-center'
	return 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400'
}

function getGenerationText() {
	if (props.type === SubscriptionType.Pro)
		return t('shop.subscription.gen_4_at_once')
	if (props.type === SubscriptionType.Basic)
		return t('shop.subscription.gen_2_at_once')
	return t('shop.subscription.gen_1_at_once')
}

function getResolutionIconClass() {
	if (props.type === SubscriptionType.Pro)
		return 'p-1 rounded-full bg-purple-500/50 flex-center'
	if (props.type === SubscriptionType.Basic)
		return 'p-1 rounded-full bg-purple-500/20 flex-center'
	return 'p-1 rounded-full bg-slate-700/20 flex-center text-slate-400'
}

function getResolutionText() {
	if (props.type === SubscriptionType.Pro)
		return t('shop.subscription.ultra_resolution')
	if (props.type === SubscriptionType.Basic)
		return t('shop.subscription.high_resolution')
	return t('shop.subscription.standard_resolution')
}

// Energy section computed
const energySection = computed(() => ({
	iconComponent: props.type !== SubscriptionType.Free ? EnergyIcon : Ticket,
	iconContainerClass: props.type !== SubscriptionType.Free
		? 'icon-container'
		: 'button-rainbow-shade !px-2 !rounded-xl !text-white',
	iconProps: props.type !== SubscriptionType.Free
		? { 'stroke': 'yellow', 'stroke-width': '2', 'variant': props.type === SubscriptionType.Pro ? 'electric' : 'normal' }
		: {},
	energyAmount: props.type === SubscriptionType.Pro ? props.energy : `${props.type === SubscriptionType.Free ? props.imagePerDay * 2 : props.energy}`,
	energyUnit: props.type === SubscriptionType.Free ? t('common.tickets') : t('common.energy'),
}))

// Title styling computed
const titleClass = computed(() => ({
	'bg-gradient-to-r from-pink-500 to-yellow-500 bg-clip-text text-transparent': props.type === SubscriptionType.Pro,
	'text-slate-200': props.type !== SubscriptionType.Pro,
}))

const plan = [
	'free_plan',
	'basic_plan',
	'pro_plan',
]

const price = computed(() => props.usd ? `$${props.price.usd}` : `${props.price.eur}€`)

const timeLeft = differenceInCalendarDays(
	new Date(useAuthStore().user.pro_expire ?? new Date()),
	new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`),
)

async function buyWithCrypto() {
	if (useAuthStore().user.plan === SubscriptionType.Basic && props.type === SubscriptionType.Pro) {
		useDynamicModalStore().showModal(DynamicModalType.RedeemConflicts, {
			props: {
				duration: Math.floor(timeLeft / 3),
				tickets: timeLeft * 10,
				serialKey: '',
				alreadyPro: false,
				crypto: true,
				usd: props.usd,
			},
		})
	}
	else {
		useDynamicModalStore().showModal(DynamicModalType.ConfirmCrypto, {
			props: {
				type: props.type,
				usd: props.usd,
			},
		})
	}
}

function buyWithG2A() {
	if (useAuthStore().isAuth()) {
		useDynamicModalStore().showModal(DynamicModalType.G2AGuide, {
			props: {
				g2aLink: props.g2a,
			},
		})
	}
	else {
		router.push('/account/signup')
	}
}
</script>

<template>
	<div
		class="group border-solid border-2 border-transparent rounded-xl my-auto h-[730px] px-6 pb-6 py-2 w-96 bg-gradient-to-b from-slate-900 to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden"
		:class="{
			'pb-[60px] pt-[60px] !h-fit': type === SubscriptionType.Pro,
			'order-1 lg:order-2': type === SubscriptionType.Pro,
			'order-2 lg:order-3': type === SubscriptionType.Basic,
			'order-3 lg:order-1': type === SubscriptionType.Free,
		}"
	>
		<!-- Background accent -->
		<div class="absolute -top-10 -right-10 w-40 h-40 bg-blue-500/10 rounded-full blur-xl" />
		<div class="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-500/10 rounded-full blur-xl" />

		<!-- Header -->
		<div class="flex justify-center w-full flex-col relative mb-2">
			<div
				class="flex items-center justify-center gap-2 text-center text-emerald-400 font-semibold text-sm mb-2 transition-opacity duration-300"
				:class="{
					'opacity-0': user.plan !== props.type,
					'opacity-100': user.plan === props.type,
				}"
			>
				<Check class="w-4 h-4" />
				{{ t('shop.current_plan') }}
			</div>
			<div
				:class="titleClass"
				class="text-3xl font-bold text-center capitalize"
			>
				{{ t(`common.${plan[props.type]}`) }}<br>
				<span
					v-if="type === SubscriptionType.Pro"
				>Unlimited</span>
			</div>
		</div>

		<!-- Pricing -->
		<div class="my-6 text-center">
			<!-- 	<span
				v-if="props.basePrice > 0"
				class="font-light line-through text-sla te-400 text-lg mr-2"
			>
				{{ basePrice }}
			</span> -->
			<template v-if="props.price[props.usd ? 'usd' : 'eur'] > 0">
				<span class="font-bold text-4xl text-slate-100">
					{{ price }}
				</span>
				<span class="text-slate-300 text-sm tracking-tight">/{{ t('durations.month') }}</span>
			</template>
			<span
				v-else
				class="font-bold text-4xl text-slate-100 uppercase"
			>{{ t('common.free') }}</span>
		</div>

		<!-- Energy section -->
		<a
			href="#energy"
			class="no-underline hover:bg-slate-700/50 cursor-pointer flex items-center gap-3 sm:h-16 h-18 bg-slate-800/50 rounded-lg p-2 my-4 group-hover:translate-y-[-4px] duration-300 transform-all"
		>
			<div :class="energySection.iconContainerClass">
				<component
					:is="energySection.iconComponent"
					v-bind="energySection.iconProps"
					:class="props.type !== SubscriptionType.Free ? 'lucide-button-icon-lg' : ''"
				/>
			</div>
			<div class="flex flex-col">
				<div class="font-semibold">
					<span class="text-lg text-slate-100">
						{{ energySection.energyAmount }}
					</span>
					<span class="font-semibold text-sm text-slate-200 ml-1"> {{ energySection.energyUnit }}</span>
					<span class="font-light text-slate-400 text-sm capitalize"> / {{ t('durations.day') }}</span>
				</div>
				<span
					class="text-xsp italic text-slate-200"
				>
					{{ t('shop.subscription.images_per_day', { count: props.imagePerDay }) }}
				</span>
			</div>
		</a>

		<!-- Divider -->
		<div class="w-full h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent my-4" />

		<!-- Features -->
		<div class="flex flex-col gap-3 font-medium mb-8">
			<div
				v-for="feature in features"
				:key="feature.key"
				class="flex items-center gap-2"
			>
				<span :class="feature.iconClass">
					<component
						:is="feature.icon"
						class="lucide-button-icon"
					/>
				</span>
				<component
					:is="feature.link ? 'a' : 'span'"
					v-bind="{
						href: `#${feature.key}`,
					}"
					:class="feature.textClass"
				>
					{{ feature.text }}
				</component :is="feature.link ? 'a' : 'span'">
			</div>
		</div>

		<div
			v-if="type !== SubscriptionType.Free"
			class="flex flex-col gap-2 items-center"
		>
			<!-- <Button
				class="button-indigo w-full"
				:disabled="useAuthStore().user.plan === SubscriptionType.Pro && type === SubscriptionType.Basic"
				@click="buyWithCrypto"
			>
				<Bitcoin class="lucide-button-icon text-black" />
				<span
					v-if="(useAuthStore().user.plan === SubscriptionType.Basic && type === SubscriptionType.Basic)
						|| (useAuthStore().user.plan === SubscriptionType.Pro && type === SubscriptionType.Pro)"
				>{{ t('shop.buttons.renew_crypto') }}</span>
				<span
					v-else-if="useAuthStore().user.plan === SubscriptionType.Basic && type === SubscriptionType.Pro"
				>{{ t('shop.buttons.upgrade_crypto') }}</span>
				<span
					v-else
				>{{ t('shop.buttons.buy_crypto') }}</span>
			</Button> -->

			<Button
				class="button-indigo w-full"
				:disabled="useAuthStore().user.plan === SubscriptionType.Pro && type === SubscriptionType.Basic"
				@click="buyWithG2A"
			>
				<img
					src="/icons/g2a.svg"
					class="w-5 h-5"
					alt="G2A"
				>
				<span
					v-if="(useAuthStore().user.plan === SubscriptionType.Basic && type === SubscriptionType.Basic)
						|| (useAuthStore().user.plan === SubscriptionType.Pro && type === SubscriptionType.Pro)"
				>{{ t('shop.buttons.renew_g2a') }}</span>
				<span
					v-else-if="useAuthStore().user.plan === SubscriptionType.Basic && type === SubscriptionType.Pro"
				>{{ t('shop.buttons.upgrade_g2a') }}</span>
				<span
					v-else
				>{{ t('shop.buttons.buy_g2a') }}</span>
			</Button>

			<span class="text-slate-300 text-xsp text-center">{{ t('shop.subscription.g2a_fee') }}</span>
			<div class="flex gap-4 text-slate-400 mt-2">
				<i class="pi pi-paypal" />
				<i class="pi pi-bitcoin" />
				<i class="pi pi-credit-card" />
			</div>
		</div>
	</div>
</template>

<style scoped>
/* .icon-container {
	@apply flex items-center justify-center w-10 h-10
	bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600
	rounded-xl shadow-md shadow-slate-950;
} */
</style>
